import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api, { getSchoolAssetUrl } from '../utils/api';
import { prepareFileForUpload } from '../utils/imageUploadCompress';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Plus, School, Edit, MapPin, Phone, Mail, Image, FlaskConical, User, Calendar, Ban } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import MobileInput from './ui/mobile-input';
import { validateMobile } from '../utils/phoneValidation';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { formatDate } from '../utils/appDateTime';
import ConfirmSchoolNameDialog from './schools/ConfirmSchoolNameDialog';

/** True when API `schools.is_demo` is set (demo / showcase tenant). */
export function schoolIsDemo(school) {
  const v = school?.is_demo;
  return v === true || v === 1 || v === '1';
}

function formatCreatedDate(value) {
  return formatDate(value, { day: '2-digit' });
}

function subscriptionStatusLabel(status) {
  if (!status || status === 'none') return 'No plan';
  return status.charAt(0).toUpperCase() + status.slice(1);
}

const Schools = () => {
  const navigate = useNavigate();
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    name: '', code: '', address: '', city: '', state: '', country: '', pincode: '',
    phone: '', email: '', website: '', logo: '', is_demo: false,
    admin_email: '', admin_first_name: '', admin_last_name: '',
  });
  const [editingSchool, setEditingSchool] = useState(null);
  const [phoneError, setPhoneError] = useState('');
  const [logoUploading, setLogoUploading] = useState(false);
  const logoInputRef = useRef(null);
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [deactivating, setDeactivating] = useState(false);

  useEffect(() => {
    fetchSchools();
    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchSchools();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  const handleLogoPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editingSchool?.id) return;
    if (!file.type.startsWith('image/')) {
      toast({ title: 'Invalid file', description: 'Choose an image (JPG, PNG, GIF, WebP)', variant: 'destructive' });
      return;
    }
    setLogoUploading(true);
    try {
      const prepared = await prepareFileForUpload(file);
      const formData = new FormData();
      formData.append('logo', prepared);
      const res = await api.post(`schools/${editingSchool.id}/logo`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const path = res.data?.data?.logo;
      if (path) setFormData((prev) => ({ ...prev, logo: path }));
      toast({ title: 'Success', description: 'Logo uploaded' });
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.message || err.message || 'Upload failed', variant: 'destructive' });
    } finally {
      setLogoUploading(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const fetchSchools = async () => {
    try {
      const response = await api.get('/schools');
      setSchools(response.data.data || []);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch schools', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validateMobile(formData.phone);
    if (err) {
      setPhoneError(err);
      toast({ title: 'Invalid mobile number', description: err, variant: 'destructive' });
      return;
    }
    setPhoneError('');
    try {
      const payload = { ...formData, phone: formData.phone || null };
      if (editingSchool) {
        await api.put(`schools/${editingSchool.id}`, payload);
        toast({ title: 'Success', description: 'School updated.' });
      } else {
        await api.post('/schools', payload);
        toast({ title: 'Success', description: 'School created. Admin credentials sent via email.' });
      }
      setIsDialogOpen(false);
      setEditingSchool(null);
      setFormData({
        name: '', code: '', address: '', city: '', state: '', country: '', pincode: '',
        phone: '', email: '', website: '', logo: '', is_demo: false,
        admin_email: '', admin_first_name: '', admin_last_name: '',
      });
      fetchSchools();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.message || 'Failed', variant: 'destructive' });
    }
  };

  const handleEdit = (school) => {
    setEditingSchool(school);
    setFormData({
      name: school.name || '', code: school.code || '', address: school.address || '', city: school.city || '',
      state: school.state || '', country: school.country || '', pincode: school.pincode || '',
      phone: school.phone || '', email: school.email || '', website: school.website || '', logo: school.logo || '',
      is_demo: schoolIsDemo(school),
      admin_email: '', admin_first_name: '', admin_last_name: '',
    });
    setIsDialogOpen(true);
  };

  const handleDeactivate = async () => {
    if (!editingSchool?.id) return;
    setDeactivating(true);
    try {
      await api.post(`schools/${editingSchool.id}/deactivate`, {
        confirm_name: editingSchool.name,
      });
      toast({
        title: 'School deactivated',
        description: `${editingSchool.name} was moved to the Bin.`,
      });
      setDeactivateOpen(false);
      setIsDialogOpen(false);
      setEditingSchool(null);
      fetchSchools();
    } catch (error) {
      toast({
        title: 'Deactivation failed',
        description: error.response?.data?.message || 'Failed to deactivate school',
        variant: 'destructive',
      });
    } finally {
      setDeactivating(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="schools-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Schools</h1>
          <p className="text-muted-foreground">Manage schools in the system</p>
          <p className="text-xs text-muted-foreground mt-1">
            Deactivated schools are in the{' '}
            <Link to="/bin?tab=schools" className="text-blue-600 hover:underline">Bin</Link>.
          </p>
          <p className="text-xs text-muted-foreground mt-2 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-amber-400/90">
              <FlaskConical className="w-3 h-3" />
              Demo
            </span>
            <span>Schools with <span className="text-muted-foreground">Demo school</span> enabled are highlighted and sorted first. Edit a school to change it.</span>
          </p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { if (!open) setEditingSchool(null); setIsDialogOpen(open); }}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-school-btn">
              <Plus className="w-4 h-4 mr-2" /> Add School
            </Button>
          </DialogTrigger>
          <DialogContent className="border-border text-foreground max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editingSchool ? 'Edit School' : 'Add New School'}</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="p-3 bg-muted/50 rounded-lg">
                <h4 className="font-medium text-foreground mb-3">School Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>School Name *</Label><Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="bg-muted border-border" required /></div>
                  <div>
                    <Label>School short name</Label>
                    <Input
                      value={formData.code}
                      onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                      className="bg-muted border-border font-mono"
                      placeholder="e.g. SMD"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Used to auto-generate teacher employee IDs (SMD-001).</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 mt-4 p-3 rounded-lg border border-amber-500/25 bg-amber-500/5">
                  <Checkbox
                    id="school-is-demo"
                    checked={!!formData.is_demo}
                    onCheckedChange={(v) => setFormData((prev) => ({ ...prev, is_demo: v === true }))}
                    className="border-amber-500/60 data-[state=checked]:bg-amber-600 data-[state=checked]:border-amber-600"
                  />
                  <div className="grid gap-0.5">
                    <Label htmlFor="school-is-demo" className="text-amber-100/90 cursor-pointer font-medium">
                      Demo school
                    </Label>
                    <p className="text-xs text-muted-foreground">Show demo styling in the schools list (for showcase / trial tenants).</p>
                  </div>
                </div>
                <div className="mt-4"><Label>Address</Label><Input value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} className="bg-muted border-border" /></div>
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div><Label>City</Label><Input value={formData.city} onChange={(e) => setFormData({...formData, city: e.target.value})} className="bg-muted border-border" /></div>
                  <div><Label>State</Label><Input value={formData.state} onChange={(e) => setFormData({...formData, state: e.target.value})} className="bg-muted border-border" /></div>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div><Label>Phone</Label><MobileInput value={formData.phone} onChange={(v) => { setFormData({ ...formData, phone: v }); setPhoneError(''); }} error={phoneError} className="bg-muted border-border" /></div>
                  <div><Label>Email</Label><Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="bg-muted border-border" /></div>
                </div>
                <div className="mt-4">
                  <Label className="flex items-center gap-2"><Image className="w-4 h-4" /> School Logo</Label>
                  <div className="flex items-center gap-3 mt-2 flex-wrap">
                    <input ref={logoInputRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" onChange={handleLogoPick} className="hidden" />
                    {editingSchool?.id && (
                      <Button type="button" variant="outline" onClick={() => logoInputRef.current?.click()} disabled={logoUploading} className="bg-muted border-border text-foreground hover:bg-muted">
                        {logoUploading ? 'Uploading...' : 'Choose image'}
                      </Button>
                    )}
                    {formData.logo && (
                      <img src={getSchoolAssetUrl(formData.logo, editingSchool?.id) || ''} alt="Logo" className="h-12 w-12 object-contain rounded border border-border bg-muted" onError={(e) => { e.target.style.display = 'none'; }} />
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">When editing: pick image to upload to assets/school/{editingSchool?.id || '…'}/. Used on fee receipts.</p>
                </div>
              </div>

              <div className="p-3 bg-muted/50 rounded-lg">
                <h4 className="font-medium text-foreground mb-3">School Admin (Optional)</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Admin First Name</Label><Input value={formData.admin_first_name} onChange={(e) => setFormData({...formData, admin_first_name: e.target.value})} className="bg-muted border-border" /></div>
                  <div><Label>Admin Last Name</Label><Input value={formData.admin_last_name} onChange={(e) => setFormData({...formData, admin_last_name: e.target.value})} className="bg-muted border-border" /></div>
                </div>
                <div className="mt-4"><Label>Admin Email</Label><Input type="email" value={formData.admin_email} onChange={(e) => setFormData({...formData, admin_email: e.target.value})} className="bg-muted border-border" placeholder="Credentials will be sent to this email" /></div>
              </div>

              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700">{editingSchool ? 'Update School' : 'Create School'}</Button>
              {editingSchool?.id ? (
                <div className="pt-2 border-t border-border">
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full text-destructive hover:text-destructive border-destructive/40"
                    onClick={() => setDeactivateOpen(true)}
                  >
                    <Ban className="w-4 h-4 mr-2" />
                    Deactivate School
                  </Button>
                  <p className="text-xs text-muted-foreground mt-2">
                    Deactivation hides the school from active listings and blocks user logins. Data is kept and can be restored from the Bin.
                  </p>
                </div>
              ) : null}
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <ConfirmSchoolNameDialog
        open={deactivateOpen}
        onOpenChange={setDeactivateOpen}
        schoolName={editingSchool?.name}
        title="Deactivate this school?"
        description={
          <p>
            The school will be deactivated and moved to the Bin. Users of this school will
            not be able to log in or use the system until it is restored. Account status for
            individual users is unchanged. No data will be deleted.
          </p>
        }
        confirmLabel="Deactivate School"
        loading={deactivating}
        onConfirm={handleDeactivate}
      />

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div></div>
      ) : schools.length === 0 ? (
        <Card className="border-border"><CardContent className="py-12 text-center">
          <School className="w-12 h-12 text-muted-foreground mx-auto mb-4" /><p className="text-muted-foreground">No schools found</p>
        </CardContent></Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {[...schools]
            .sort((a, b) => {
              const ta = new Date(a.created_at || 0).getTime();
              const tb = new Date(b.created_at || 0).getTime();
              if (tb !== ta) return tb - ta;
              const da = schoolIsDemo(a) ? 0 : 1;
              const db = schoolIsDemo(b) ? 0 : 1;
              if (da !== db) return da - db;
              return String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' });
            })
            .map((school) => {
            const demo = schoolIsDemo(school);
            const inactive = school.status === 'inactive';
            const logoUrl = getSchoolAssetUrl(school.logo, school.id);
            return (
            <Card
              key={school.id}
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/schools/${school.id}`)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/schools/${school.id}`); } }}
              className={`border-border hover:border-border cursor-pointer ${
                demo ? 'ring-2 ring-amber-500/35 border-amber-500/30 shadow-[0_0_0_1px_rgba(245,158,11,0.12)]' : ''
              }`}
            >
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden ${demo ? 'bg-amber-500/20' : 'bg-blue-500/20'}`}>
                    {logoUrl ? (
                      <img src={logoUrl} alt="" className="w-full h-full object-contain" onError={(e) => { e.target.style.display = 'none'; e.target.nextElementSibling?.classList.remove('hidden'); }} />
                    ) : null}
                    <div className={`w-full h-full flex items-center justify-center ${logoUrl ? 'hidden' : ''}`}>
                      <School className={`w-7 h-7 ${demo ? 'text-amber-400' : 'text-blue-500'}`} />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <h3 className="font-semibold text-foreground text-lg truncate">{school.name}</h3>
                        {demo && (
                          <Badge className="shrink-0 border-amber-500/50 bg-amber-500/15 text-amber-300 hover:bg-amber-500/20 gap-1">
                            <FlaskConical className="w-3 h-3" />
                            Demo school
                          </Badge>
                        )}
                        {inactive && (
                          <Badge className="shrink-0 border-amber-500/50 bg-amber-500/15 text-amber-400 hover:bg-amber-500/20">
                            Awaiting plan
                          </Badge>
                        )}
                      </div>
                      <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); handleEdit(school); }} className="text-muted-foreground hover:text-foreground shrink-0">
                        <Edit className="w-4 h-4" />
                      </Button>
                      <span className={`px-2 py-1 rounded-full text-xs shrink-0 ${school.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-amber-500/15 text-amber-400'}`}>
                        {school.status}
                      </span>
                    </div>
                    {school.code && (
                      <p className={`text-sm mt-1 ${demo ? 'text-amber-200/80' : 'text-muted-foreground'}`}>
                        Code: <span className="font-mono">{school.code}</span>
                      </p>
                    )}
                    {school.address && (
                      <div className="flex items-center gap-1 mt-2 text-sm text-muted-foreground">
                        <MapPin className="w-3 h-3" />{school.address}, {school.city}
                      </div>
                    )}
                    <div className="flex items-center gap-4 mt-2 flex-wrap">
                      {school.phone && <span className="flex items-center gap-1 text-xs text-muted-foreground"><Phone className="w-3 h-3" />{school.phone}</span>}
                      {school.email && <span className="flex items-center gap-1 text-xs text-muted-foreground"><Mail className="w-3 h-3" />{school.email}</span>}
                    </div>
                    {school.admin_name && (
                      <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
                        <User className="w-3 h-3" />
                        <span>Admin: {school.admin_name}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <Badge variant="outline" className="text-xs">
                        {school.current_plan_name || 'No plan'}
                      </Badge>
                      <Badge
                        variant="outline"
                        className={`text-xs ${
                          school.subscription_status === 'active' || school.subscription_status === 'trial'
                            ? 'border-green-500/40 text-green-600'
                            : school.subscription_status === 'none'
                              ? 'border-muted-foreground/30 text-muted-foreground'
                              : 'border-amber-500/40 text-amber-600'
                        }`}
                      >
                        {subscriptionStatusLabel(school.subscription_status)}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
                      <Calendar className="w-3 h-3" />
                      <span>Created {formatCreatedDate(school.created_at)}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Schools;
